import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Fade,
} from "@mui/material";
import {
    Email as EmailIcon,
    Schedule as ScheduleIcon,
    LocationOn as LocationOnIcon,
    Send as SendIcon,
} from "@mui/icons-material";
import { database } from "../../firebase";
import { ref, push, set } from "firebase/database";
import SEO from "../../components/SEO/SEO";
import { pageMeta } from "../../seo/pageMeta";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const { name, email, subject, message } = formData;

        if (
            !name.trim() ||
            !email.trim() ||
            !subject.trim() ||
            !message.trim()
        ) {
            setSubmitMessage("Please fill in all required fields.");
            setMessageType("error");
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setSubmitMessage("Please enter a valid email address.");
            setMessageType("error");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitMessage("");

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            // Simulate form submission
            await new Promise((resolve) => setTimeout(resolve, 1000));
            const messagesRef = ref(database, "contactMessages");
            const newMessageRef = push(messagesRef);
            await set(newMessageRef, {
                name: formData.name.trim(),
                email: formData.email.trim(),
                subject: formData.subject.trim(),
                message: formData.message.trim(),
                timestamp: new Date().toISOString(),
            });

            setSubmitMessage(
                "Thank you for your message! We will get back to you within 24-48 hours."
            );
            setMessageType("success");
            setFormData({ name: "", email: "", subject: "", message: "" });
        } catch (error) {
            setSubmitMessage(
                "Sorry, there was an error sending your message. Please try again."
            );
            setMessageType("error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                py: { xs: 6, md: 8 },
                background:
                    "linear-gradient(135deg, rgba(245, 245, 245, 0.8) 0%, rgba(255, 255, 255, 1) 100%)",
                minHeight: "calc(100vh - 200px)",
            }}
        >
            <SEO {...pageMeta.contact} />
            <Container maxWidth="lg">
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                        }}
                    >
                        Get in Touch
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 600,
                            mx: "auto",
                            lineHeight: 1.6,
                        }}
                    >
                        Have questions, suggestions, or need support? We'd love
                        to hear from you.
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                        <Fade in timeout={800}>
                            <Card sx={{ p: { xs: 3, md: 4 } }}>
                                <CardContent>
                                    <Typography
                                        variant="h4"
                                        component="h2"
                                        sx={{
                                            mb: 4,
                                            textAlign: "center",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Send us a Message
                                    </Typography>

                                    <Box
                                        component="form"
                                        onSubmit={handleSubmit}
                                    >
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Full Name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Email Address"
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Subject"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleInputChange}
                                                    required
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Message"
                                                    name="message"
                                                    multiline
                                                    rows={6}
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    required
                                                    variant="outlined"
                                                    placeholder="Tell us how we can help you..."
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    size="large"
                                                    disabled={isLoading}
                                                    startIcon={
                                                        isLoading ? (
                                                            <CircularProgress
                                                                size={20}
                                                                color="inherit"
                                                            />
                                                        ) : (
                                                            <SendIcon />
                                                        )
                                                    }
                                                    fullWidth
                                                    sx={{
                                                        py: 1.5,
                                                        fontSize: "1rem",
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {isLoading
                                                        ? "Sending Message..."
                                                        : "Send Message"}
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        {submitMessage && (
                                            <Fade in>
                                                <Alert
                                                    severity={messageType}
                                                    sx={{
                                                        mt: 3,
                                                        borderRadius: 2,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {submitMessage}
                                                </Alert>
                                            </Fade>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Fade>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Fade in timeout={1200}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                }}
                            >
                                <Card>
                                    <CardContent
                                        sx={{ textAlign: "center", p: 4 }}
                                    >
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                mb: 3,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Other Ways to Reach Us
                                        </Typography>

                                        <Box sx={{ mb: 3 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: "primary.main",
                                                    borderRadius: "50%",
                                                    mx: "auto",
                                                    mb: 2,
                                                }}
                                            >
                                                <EmailIcon
                                                    sx={{ color: "white" }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, mb: 1 }}
                                            >
                                                Email Support
                                            </Typography>
                                            <Typography
                                                component="a"
                                                href="mailto:admin@quranchatbuddy.com"
                                                sx={{
                                                    color: "primary.main",
                                                    textDecoration: "none",
                                                    fontWeight: 500,
                                                    "&:hover": {
                                                        textDecoration:
                                                            "underline",
                                                    },
                                                }}
                                            >
                                                admin@quranchatbuddy.com
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                            >
                                                We typically respond within
                                                24-48 hours
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mb: 3 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: "secondary.main",
                                                    borderRadius: "50%",
                                                    mx: "auto",
                                                    mb: 2,
                                                }}
                                            >
                                                <ScheduleIcon
                                                    sx={{ color: "white" }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, mb: 1 }}
                                            >
                                                Support Hours
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Monday - Friday: 9 AM - 5 PM GMT
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Weekend messages answered on
                                                Monday
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: "info.main",
                                                    borderRadius: "50%",
                                                    mx: "auto",
                                                    mb: 2,
                                                }}
                                            >
                                                <LocationOnIcon
                                                    sx={{ color: "white" }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 600, mb: 1 }}
                                            >
                                                Islamic Guidance
                                            </Typography>
                                            <Typography
                                                component="a"
                                                href="mailto:admin@quranchatbuddy.com"
                                                sx={{
                                                    color: "primary.main",
                                                    textDecoration: "none",
                                                    fontWeight: 500,
                                                    "&:hover": {
                                                        textDecoration:
                                                            "underline",
                                                    },
                                                }}
                                            >
                                                admin@quranchatbuddy.com
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ mt: 1 }}
                                            >
                                                For questions about Islamic
                                                content
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Fade>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Contact;
